import { BaseEntity } from '../base-entity';
import { IdentityPrincipal } from './identity-principal';
import { PbiDataset } from './pbi-dataset';
import { PbiOrganisationWorkspace } from './pbi-organisation-workspace';
import { Report } from './report';

/// <code-import> Place custom imports between <code-import> tags
import { EntityName } from '@mri-platform/dsg/core';
import { AuthzContext, AuthzContextsActionMap, CrudAction, CrudAllAction } from '@mri-platform/resource-authz';
/// </code-import>

export interface PbiReport {
  id: string;
  createdDate: Date;
  description?: string;
  embedUrl: string;
  isDeleted: boolean;
  isDraft: boolean;
  lastUpdatedBy: string;
  lastUpdatedDate: Date;
  name: string;
  ownerId?: string;
  pbiDatasetId: string;
  pbiWorkspaceId: string;
  periodEnd: Date;
  periodStart: Date;
  reportId: string;
  tenantKey: string;
  webUrl: string;
  owner: IdentityPrincipal;
  pbiDataset: PbiDataset;
  pbiWorkspace: PbiOrganisationWorkspace;
  report: Report;
}

/// <module-code> Place module level code between <module-code> tags
export enum PbiReportCustomAction {
  Download = 'Download',
  Import = 'Import'
}
const typeFuncs = BaseEntity.makeStaticTypeFunctions<PbiReport>(EntityName.PbiReport);

export interface AskAgoraReport {
  reportId: string;
  pbiDatasetId: string;
  report_OrganisationWorkspace_TemplateWorkspace_Name: string;
  report_Category: string;
}
/// </module-code>

export class PbiReport extends BaseEntity {
  /// <code> Place custom code between <code> tags
  constructor() {
    super();
    this.createdDate = new Date();
  }

  static propName = typeFuncs.propName;
  static propNames = typeFuncs.propNames;
  static qualifiedPropName = typeFuncs.qualifiedPropName;
  static qualifiedPropNames = typeFuncs.qualifiedPropNames;

  static authorization: AuthzContextsActionMap = {
    ...AuthzContextsActionMap.crudFor(EntityName.PbiReport),
    [PbiReportCustomAction.Download]: {
      action: PbiReportCustomAction.Download,
      resource: EntityName.PbiReport
    },
    [PbiReportCustomAction.Import]: {
      action: PbiReportCustomAction.Import,
      resource: EntityName.PbiReport
    },
    [CrudAction.Delete]: AuthzContext.updateFor(PbiReport.qualifiedPropName('isDeleted')),
    [CrudAllAction.Delete]: AuthzContext.updateAllFor(PbiReport.qualifiedPropName('isDeleted'))
  };

  static draftPrefix = '[Draft] ';
  static unpublishedPrefix = '[Unpublished] ';

  static propPath<
    K1 extends keyof PbiReport,
    K2 extends keyof PbiReport[K1],
    K3 extends keyof PbiReport[K1][K2],
    K4 extends keyof PbiReport[K1][K2][K3]
  >(k1: K1, k2: K2, k3: K3, k4: K4): string;
  static propPath<K1 extends keyof PbiReport, K2 extends keyof PbiReport[K1], K3 extends keyof PbiReport[K1][K2]>(
    k1: K1,
    k2: K2,
    k3: K3
  ): string;
  static propPath<K1 extends keyof PbiReport, K2 extends keyof PbiReport[K1]>(k1: K1, k2: K2): string;
  static propPath(...keys: string[]) {
    return super.getPropPath(keys);
  }

  static baseReportName(pbiReportName: string) {
    return pbiReportName.replace(this.draftPrefix, '');
  }
  /// </code>
  // Generated code. Do not place code below this line.
}
